import { addMonths } from 'date-fns/addMonths'
import { addQuarters } from 'date-fns/addQuarters'
import { addWeeks } from 'date-fns/addWeeks'
import { addYears } from 'date-fns/addYears'
import { endOfMonth } from 'date-fns/endOfMonth'
import { getDay } from 'date-fns/getDay'
import { isBefore } from 'date-fns/isBefore'
import { nextDay } from 'date-fns/nextDay'
import { setDate } from 'date-fns/setDate'
import { startOfToday } from 'date-fns/startOfToday'
import { fromUnixTime } from 'date-fns/fromUnixTime'
import { type Day } from 'date-fns'

import { DATE_FORMATS_INTL, formatDate } from '../constants'

export const getNextWeeklyDate = (date: Date): Date => {
  const dayOfWeek = getDay(date)
  let nextDate = nextDay(date, dayOfWeek as Day)
  while (isBefore(nextDate, startOfToday())) {
    nextDate = addWeeks(nextDate, 1)
  }
  return nextDate
}

export const getNextBiWeeklyDate = (date: Date): Date => {
  let nextDate = date
  while (isBefore(nextDate, startOfToday())) {
    nextDate = addWeeks(nextDate, 2)
  }
  return nextDate
}

export const getTwiceMonthlyDates = (day1: number, day2: number): Date[] => {
  const today = startOfToday()
  const [firstDay, secondDay] = [day1, day2].sort((a, b) => a - b)
  const makeDateForDay = (month: Date, day: number) =>
    day >= 28 ? endOfMonth(month) : setDate(month, day)

  return [
    makeDateForDay(today, firstDay),
    makeDateForDay(today, secondDay),
    makeDateForDay(addMonths(today, 1), firstDay),
    makeDateForDay(addMonths(today, 1), secondDay),
    makeDateForDay(addMonths(today, 2), firstDay),
    makeDateForDay(addMonths(today, 2), secondDay),
  ]
}

export const getNextTwiceMonthlyDate = (day1: number, day2: number): Date => {
  const today = startOfToday()
  const candidates = getTwiceMonthlyDates(day1, day2)
  return candidates.find((c) => !isBefore(c, today)) ?? candidates[candidates.length - 1]
}

export const getNextMonthlyDate = (date: Date): Date => {
  let nextDate = setDate(startOfToday(), date.getDate())
  while (isBefore(nextDate, startOfToday())) {
    nextDate = addMonths(nextDate, 1)
  }
  return nextDate
}

export const getNextBiMonthlyDate = (date: Date): Date => {
  let nextDate = date
  while (isBefore(nextDate, startOfToday())) {
    nextDate = addMonths(nextDate, 2)
  }
  return nextDate
}

export const getNextQuarterlyDate = (date: Date): Date => {
  let nextDate = addQuarters(date, 1)
  while (isBefore(nextDate, startOfToday())) {
    nextDate = addQuarters(nextDate, 1)
  }
  return nextDate
}

export const getNextBiQuarterlyDate = (date: Date): Date => {
  let nextDate = date
  while (isBefore(nextDate, startOfToday())) {
    nextDate = addQuarters(nextDate, 2)
  }
  return nextDate
}

export const getNextYearlyDate = (date: Date): Date => {
  return addYears(date, 1)
}

export const fromUnixUTC = (utcTime: number) => {
  const date = fromUnixTime(utcTime)
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

// Inverse of fromUnixUTC: emits unix seconds for the Date's wall-clock time read
// at UTC, so a local-midnight boundary serializes to UTC midnight instead of
// being shifted by the viewer's timezone offset.
export const toUnixUTC = (date: Date) =>
  Math.floor(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ) / 1000,
  )

export const fromUnixToDayOfWeekAndDate = (date: number) =>
  formatDate(fromUnixTime(date), DATE_FORMATS_INTL.DAY_OF_WEEK_MONTH_DAY)

// UTC variant for post dates, which are absolute calendar dates that must not
// shift across the day boundary when localized.
export const fromUnixUTCToDayOfWeekAndDate = (date: number) =>
  formatDate(fromUnixUTC(date), DATE_FORMATS_INTL.DAY_OF_WEEK_MONTH_DAY)

export const fromTimestampToMonthYear = (timestamp?: number) =>
  timestamp ? formatDate(fromUnixTime(timestamp), DATE_FORMATS_INTL.MONTH_YEAR) : ''
